import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useEvidence(profileId, orgId) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const subRef = useRef(null);

  const loadEvidence = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase
      .from('evidence')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    setEvidence(data || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    loadEvidence();

    subRef.current = supabase
      .channel('evidence-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'evidence' }, () => {
        loadEvidence();
      })
      .subscribe();

    return () => {
      if (subRef.current) supabase.removeChannel(subRef.current);
    };
  }, [orgId, loadEvidence]);

  async function addEvidence(entry) {
    const { data, error } = await supabase
      .from('evidence')
      .insert({ ...entry, org_id: orgId })
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  // Save a new draft — same as addEvidence but status='draft'
  async function saveDraft(entry) {
    const { data, error } = await supabase
      .from('evidence')
      .insert({ ...entry, org_id: orgId, status: 'draft' })
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  // Update an existing draft (or promote draft → pending)
  async function updateDraft(id, updates) {
    const { data, error } = await supabase
      .from('evidence')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    // Reload to keep list fresh
    await loadEvidence();
    return data;
  }

  async function updateEvidence(id, updates) {
    const { error } = await supabase
      .from('evidence')
      .update(updates)
      .eq('id', id);
    if (error) throw error;
    // Update local state immediately so the UI reflects the change without waiting for refetch
    setEvidence(prev => prev.map(e => (e.id === id ? { ...e, ...updates } : e)));
    // Refetch in the background to make sure we're in sync with the server
    loadEvidence();
  }

  async function deleteEvidence(id) {
    const { error } = await supabase
      .from('evidence')
      .delete()
      .eq('id', id);
    if (error) throw error;
    // Update local state immediately
    setEvidence(prev => prev.filter(e => e.id !== id));
    loadEvidence();
  }

  return {
    evidence,
    loading,
    addEvidence,
    saveDraft,
    updateDraft,
    updateEvidence,
    deleteEvidence,
    reload: loadEvidence,
  };
}
